import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Row,
  Col,
  Image,
  Alert,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  Eye,
  Search,
  ChevronUp,
  ChevronDown,
  InfoCircleFill,
} from "react-bootstrap-icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../App.css";
import LOGO_URL from "../img/tmis_logo_orig.png";

const NAV_BLUE = "#183B72";
const PAGE_BG = "#F5F7FB";
const BORDER = "#E5E7EB";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_USER_RENEWAL = `${API_BASE}/getUserUpdateExpiry`;
const ENDPOINT_ADMIN_UPDATE = `${API_BASE}/adminUpdateUserLicence`;
const ENDPOINT_CREATE_CYCLE = `${API_BASE}/tgCreateCycle`;

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";

  return { displayName };
}

function getRowId(row) {
  if (!row) return "";
  return row.ebId || row.id || "";
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);

  let bg = "#E5E7EB";
  let color = "#374151";

  if (normalized === "approved") {
    bg = "#DCFCE7";
    color = "#15803D";
  } else if (
    normalized === "under review" ||
    normalized === "pending" ||
    normalized === "submitted"
  ) {
    bg = "#FEF3C7";
    color = "#B45309";
  } else if (normalized === "rejected") {
    bg = "#FEE2E2";
    color = "#B91C1C";
  } else if (normalized === "cancelled" || normalized === "canceled") {
    bg = "#E5E7EB";
    color = "#4B5563";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 95,
        padding: "6px 14px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        background: bg,
        color,
        textTransform: "capitalize",
      }}
    >
      {status || "-"}
    </span>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#1F2937",
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: "#111827",
          minHeight: 22,
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </div>
    </div>
  );
}

function parseDisplayDate(value) {
  if (!value) return null;

  const str = String(value).trim();
  const match = str.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);

  if (match) {
    const [, dd, mm, yyyy, hh = "00", min = "00"] = match;
    const date = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      0,
    );
    return isNaN(date.getTime()) ? null : date;
  }

  const nativeDate = new Date(str);
  return isNaN(nativeDate.getTime()) ? null : nativeDate;
}

function compareValues(a, b, direction = "asc", type = "text") {
  let result = 0;

  if (type === "date") {
    const aDate = parseDisplayDate(a);
    const bDate = parseDisplayDate(b);

    const aTime = aDate ? aDate.getTime() : -Infinity;
    const bTime = bDate ? bDate.getTime() : -Infinity;

    result = aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
  } else {
    const aVal = (a ?? "").toString().toLowerCase();
    const bVal = (b ?? "").toString().toLowerCase();

    result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  }

  return direction === "asc" ? result : -result;
}

function SortableHeader({ label, sortKey, sortConfig, onSort }) {
  const isActive = sortConfig.key === sortKey;
  const isAsc = isActive && sortConfig.direction === "asc";

  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: "16px 14px",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontWeight: 700,
        }}
      >
        <span>{label}</span>
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {isActive ? (
            isAsc ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )
          ) : (
            <span style={{ opacity: 0.35, display: "inline-flex" }}>
              <ChevronDown size={14} />
            </span>
          )}
        </span>
      </div>
    </th>
  );
}

function normalizeServerImageUrl(pathValue) {
  if (!pathValue) return "";

  let val = String(pathValue).trim();
  if (!val) return "";

  val = val.replace(/\\/g, "/");

  if (/^https?:\/\//i.test(val)) return val;

  if (val.startsWith("/")) {
    return `${API_BASE}${val}`;
  }

  if (val.startsWith("uploads/")) {
    return `${API_BASE}/${val}`;
  }

  const marker = "/uploads/";
  const idx = val.toLowerCase().indexOf(marker);
  if (idx !== -1) {
    const relativePath = val.substring(idx);
    return `${API_BASE}${relativePath}`;
  }

  return val;
}

export default function Renewal() {
  const { displayName } = useMemo(getStoredUser, []);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [rows, setRows] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdDate",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailImageUrl, setDetailImageUrl] = useState("");

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth_token") ||
    "";

  const authHeaders = useMemo(() => {
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [token]);

  const loadRenewal = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      if (!API_BASE) {
        setRows([]);
        setErr("API base URL is missing.");
        return;
      }

      const { data } = await axios.get(ENDPOINT_USER_RENEWAL, {
        headers: authHeaders,
        timeout: 20000,
      });

      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setRows([]);
      setErr(
        e?.response?.data?.message || "Server error. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadRenewal();
  }, [loadRenewal]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredRows = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return rows;

    return rows.filter((row) => {
      const searchableText = [
        row.fullname,
        row.badge_no,
        row.ref_number,
        row.currExpiryDate,
        row.newExpiryDate,
        row.createdDate,
        row.status,
        row.remarks,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(keyword);
    });
  }, [rows, searchTerm]);

  const sortedRows = useMemo(() => {
    const copied = [...filteredRows];

    copied.sort((a, b) => {
      switch (sortConfig.key) {
        case "fullname":
          return compareValues(a.fullname, b.fullname, sortConfig.direction);
        case "badge_no":
          return compareValues(a.badge_no, b.badge_no, sortConfig.direction);
        case "ref_number":
          return compareValues(
            a.ref_number,
            b.ref_number,
            sortConfig.direction,
          );
        case "currExpiryDate":
          return compareValues(
            a.currExpiryDate,
            b.currExpiryDate,
            sortConfig.direction,
            "date",
          );
        case "newExpiryDate":
          return compareValues(
            a.newExpiryDate,
            b.newExpiryDate,
            sortConfig.direction,
            "date",
          );
        case "createdDate":
          return compareValues(
            a.createdDate,
            b.createdDate,
            sortConfig.direction,
            "date",
          );
        case "status":
          return compareValues(a.status, b.status, sortConfig.direction);
        default:
          return 0;
      }
    });

    return copied;
  }, [filteredRows, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: "asc",
      };
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => navigate(-1);

  const loadRenewalDetail = async (ebId) => {
    try {
      setDetailLoading(true);
      setDetailImageUrl("");

      const { data } = await axios.get(`${ENDPOINT_USER_RENEWAL}?id=${ebId}`, {
        headers: authHeaders,
        timeout: 20000,
      });

      const imageUrl = normalizeServerImageUrl(data?.key || "");
      setDetailImageUrl(imageUrl);
    } catch (e) {
      setDetailImageUrl("");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleView = async (row) => {
    console.log("Selected row:", row);
    setSelectedRow(row);
    setRemarks(row?.remarks || "");
    setSuccessMsg("");
    setShowViewModal(true);
    await loadRenewalDetail(getRowId(row));
  };

  const closeModal = () => {
    if (savingStatus) return;
    setShowViewModal(false);
    setSelectedRow(null);
    setRemarks("");
    setSuccessMsg("");
    setDetailImageUrl("");
    setDetailLoading(false);
  };

  const submitStatus = async (status) => {
    const recordId = getRowId(selectedRow);
    const userId = selectedRow?.user_id;

    if (!recordId) {
      setErr("Record ID not found.");
      return;
    }

    if (normalizeStatus(status) === "approved" && !userId) {
      setErr("User ID not found. Unable to create cycle.");
      return;
    }

    try {
      setSavingStatus(true);
      setSuccessMsg("");
      setErr("");

      const payload = {
        id: recordId,
        status,
        remarks: remarks || "",
      };

      const updateRes = await axios.post(ENDPOINT_ADMIN_UPDATE, payload, {
        headers: authHeaders,
        timeout: 20000,
      });

      if (!updateRes?.data?.success) {
        setErr(updateRes?.data?.message || "Unable to update status.");
        return;
      }

      const approvedNow =
        normalizeStatus(status) === "approved"
          ? new Date().toLocaleString()
          : selectedRow?.approved_date || "";

      setRows((prev) =>
        prev.map((item) => {
          const itemId = getRowId(item);
          if (String(itemId) === String(recordId)) {
            return {
              ...item,
              status,
              remarks: remarks || "",
              approved_date: approvedNow,
            };
          }
          return item;
        }),
      );

      setSelectedRow((prev) =>
        prev
          ? {
              ...prev,
              status,
              remarks: remarks || "",
              approved_date: approvedNow,
            }
          : prev,
      );

      if (normalizeStatus(status) === "approved") {
        try {
          console.log("Calling tgCreateCycle with userId:", userId);

          const cycleRes = await axios.post(
            ENDPOINT_CREATE_CYCLE,
            { userId },
            {
              headers: authHeaders,
              timeout: 20000,
            },
          );

          console.log("tgCreateCycle response:", cycleRes?.data);

          if (!cycleRes?.data?.success) {
            setErr(
              cycleRes?.data?.message ||
                "Status updated, but cycle was not created.",
            );
            return;
          }

          setSuccessMsg(
            `${updateRes?.data?.message || "Successfully updated!"} Cycle created successfully.`,
          );
        } catch (cycleError) {
          console.error("Cycle creation failed:", cycleError);
          setErr(
            cycleError?.response?.data?.message ||
              "Status updated, but cycle creation failed.",
          );
          return;
        }
      } else {
        setSuccessMsg(updateRes?.data?.message || "Successfully updated!");
      }

      await loadRenewal();
    } catch (e) {
      setErr(
        e?.response?.data?.message || "Server error. Please try again later.",
      );
    } finally {
      setSavingStatus(false);
    }
  };

  const currentStatus = normalizeStatus(selectedRow?.status);
  const isCancelledStatus =
    currentStatus === "cancelled" || currentStatus === "canceled";
  const isFinalStatus =
    currentStatus === "approved" ||
    currentStatus === "rejected" ||
    isCancelledStatus;
  const canAdminUpdateStatus =
    !savingStatus &&
    !isFinalStatus &&
    (currentStatus === "under review" ||
      currentStatus === "pending" ||
      currentStatus === "submitted");

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: PAGE_BG }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center justify-content-between"
            style={{ gap: 12 }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <span
                role="button"
                aria-label="Go back"
                tabIndex={0}
                onClick={handleBack}
                onKeyDown={(e) =>
                  e.key === "Enter" || e.key === " " ? handleBack() : null
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  background: "transparent",
                  cursor: "pointer",
                  outline: "none",
                }}
                className="back-icon"
              >
                <ArrowLeft size={18} color="#ffffff" />
              </span>
              <h4 className="m-0 fw-bold">Update Expiry Date Management</h4>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        {err ? (
          <div
            style={{
              marginBottom: 16,
              background: "#FEE2E2",
              color: "#B91C1C",
              padding: "12px 14px",
              borderRadius: 10,
              fontWeight: 600,
            }}
          >
            {err}
          </div>
        ) : null}

        <div
          style={{
            background: "#FFFFFF",
            borderRadius: 18,
            border: `1px solid ${BORDER}`,
            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
            overflow: "hidden",
          }}
        >
          <div
            className="d-flex flex-column flex-md-row justify-content-between align-items-md-center"
            style={{
              gap: 12,
              padding: 18,
              borderBottom: `1px solid ${BORDER}`,
              background: "#fff",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                Change Expiry Date Requests
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#1F2937",
                  marginTop: 4,
                }}
              >
                Search, sort, and review expiry date update requests.
              </div>
            </div>

            <div style={{ width: "100%", maxWidth: 320 }}>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    background: "#fff",
                    borderRight: "none",
                  }}
                >
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, badge, ref..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderLeft: "none",
                    boxShadow: "none",
                  }}
                />
              </InputGroup>
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead style={{ background: "#F8FAFC" }}>
                <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <th style={{ padding: "16px 14px", whiteSpace: "nowrap" }}>
                    No
                  </th>
                  <SortableHeader
                    label="Full Name"
                    sortKey="fullname"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Badge No"
                    sortKey="badge_no"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Reference"
                    sortKey="ref_number"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Old Exp Date"
                    sortKey="currExpiryDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="New Exp Date"
                    sortKey="newExpiryDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Submitted Date"
                    sortKey="createdDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <th style={{ padding: "16px 14px", whiteSpace: "nowrap" }}>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ padding: 30, textAlign: "center" }}
                    >
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ padding: 30, textAlign: "center" }}
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, index) => (
                    <tr
                      key={getRowId(row) || index}
                      style={{ borderBottom: `1px solid ${BORDER}` }}
                    >
                      <td style={{ padding: "14px" }}>
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </td>
                      <td style={{ padding: "14px" }}>{row.fullname || "-"}</td>
                      <td style={{ padding: "14px" }}>{row.badge_no || "-"}</td>
                      <td style={{ padding: "14px" }}>
                        {row.ref_number || "-"}
                      </td>
                      <td style={{ padding: "14px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {row.currExpiryDate || "-"}
                          {row.tgExpiryDate &&
                          row.tgExpiryDate !== row.currExpiryDate ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  Current licence expiry date:{" "}
                                  {row.tgExpiryDate}
                                </Tooltip>
                              }
                            >
                              <InfoCircleFill
                                size={16}
                                color="#183B72"
                                style={{ cursor: "pointer" }}
                              />
                            </OverlayTrigger>
                          ) : null}
                        </span>
                      </td>
                      <td style={{ padding: "14px" }}>
                        {row.newExpiryDate || "-"}
                      </td>
                      <td style={{ padding: "14px" }}>
                        {row.createdDate || "-"}
                      </td>
                      <td style={{ padding: "14px" }}>
                        <StatusPill status={row.status} />
                      </td>
                      <td style={{ padding: "14px" }}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleView(row)}
                          style={{
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            minWidth: 88,
                            justifyContent: "center",
                          }}
                        >
                          <Eye size={14} />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div
            className="d-flex flex-column flex-md-row justify-content-between align-items-md-center"
            style={{
              gap: 12,
              padding: 18,
              borderTop: `1px solid ${BORDER}`,
              background: "#fff",
            }}
          >
            <div style={{ fontSize: 14, color: "#1F2937" }}>
              Showing{" "}
              <strong>
                {sortedRows.length === 0
                  ? 0
                  : (currentPage - 1) * rowsPerPage + 1}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(currentPage * rowsPerPage, sortedRows.length)}
              </strong>{" "}
              of <strong>{sortedRows.length}</strong> entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                style={{ borderRadius: 8, minWidth: 80 }}
              >
                Previous
              </Button>

              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#374151",
                  minWidth: 90,
                  textAlign: "center",
                }}
              >
                Page {currentPage} / {totalPages}
              </span>

              <Button
                variant="outline-secondary"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                style={{ borderRadius: 8, minWidth: 80 }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Container>

      <Modal
        show={showViewModal}
        onHide={closeModal}
        centered
        size="lg"
        backdrop={savingStatus ? "static" : true}
        keyboard={!savingStatus}
        className="custom-font"
      >
        <Modal.Header closeButton={!savingStatus}>
          <Modal.Title style={{ fontWeight: 700 }}>
            Update Expiry Date Request
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: "#F9FAFB" }}>
          {successMsg ? (
            <Alert variant="success" style={{ fontWeight: 600 }}>
              {successMsg}
            </Alert>
          ) : null}

          {selectedRow ? (
            <>
              {isCancelledStatus ? (
                <Alert variant="secondary" style={{ fontWeight: 600 }}>
                  This request has been cancelled. Reject and approve actions are disabled.
                </Alert>
              ) : isFinalStatus ? (
                <Alert variant="info" style={{ fontWeight: 600 }}>
                  This request is already {selectedRow.status}. No further status update is allowed.
                </Alert>
              ) : null}

              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: 18,
                  border: "1px solid #E5E7EB",
                  marginBottom: 16,
                }}
              >
                <Row>
                  <Col md={6}>
                    <DetailItem
                      label="Full Name"
                      value={selectedRow.fullname}
                    />
                  </Col>
                  <Col md={6}>
                    <DetailItem label="Status" value={selectedRow.status} />
                  </Col>

                  <Col md={6}>
                    <DetailItem label="Badge No" value={selectedRow.badge_no} />
                  </Col>

                  <Col md={6}>
                    <DetailItem
                      label="Reference Number"
                      value={selectedRow.ref_number}
                    />
                  </Col>

                  <Col md={6}>
                    <DetailItem
                      label="Submitted Date"
                      value={selectedRow.createdDate}
                    />
                  </Col>

                  <Col md={6}>
                    <DetailItem
                      label="Old Expiry Date"
                      value={selectedRow.currExpiryDate}
                    />
                  </Col>

                  <Col md={6}>
                    <DetailItem
                      label="New Expiry Date"
                      value={selectedRow.newExpiryDate}
                    />
                  </Col>

                  <Col md={6}>
                    <DetailItem
                      label="Approved Date"
                      value={selectedRow.approved_date}
                    />
                  </Col>

                  <Col md={12}>
                    <DetailItem
                      label="Existing Remarks"
                      value={selectedRow.remarks}
                    />
                  </Col>
                </Row>
              </div>

              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: 18,
                  border: "1px solid #E5E7EB",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 12,
                  }}
                >
                  Uploaded Image
                </div>

                {detailLoading ? (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading image...
                  </div>
                ) : detailImageUrl ? (
                  <Row>
                    <Col md={12} className="mb-2">
                      <a
                        href={detailImageUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <Image
                          src={detailImageUrl}
                          alt="Uploaded document"
                          fluid
                          rounded
                          style={{
                            width: "100%",
                            maxHeight: 420,
                            objectFit: "contain",
                            background: "#F3F4F6",
                            border: "1px solid #E5E7EB",
                            padding: 8,
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src =
                              "data:image/svg+xml;utf8," +
                              encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" width="600" height="320">
                                  <rect width="100%" height="100%" fill="#F3F4F6"/>
                                  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                                    fill="#6B7280" font-size="20" font-family="Arial, sans-serif">
                                    Unable to load image
                                  </text>
                                </svg>
                              `);
                          }}
                        />
                      </a>
                    </Col>
                  </Row>
                ) : (
                  <div
                    style={{
                      background: "#F9FAFB",
                      border: "1px dashed #D1D5DB",
                      borderRadius: 12,
                      padding: 18,
                      color: "#1F2937",
                      textAlign: "center",
                    }}
                  >
                    No uploaded image found.
                  </div>
                )}
              </div>

              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: 18,
                  border: "1px solid #E5E7EB",
                }}
              >
                <Form.Group>
                  <Form.Label
                    style={{
                      fontWeight: 700,
                      color: "#111827",
                      marginBottom: 8,
                    }}
                  >
                    Remarks
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter remarks..."
                    value={remarks}
                    disabled={!canAdminUpdateStatus}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={{
                      borderRadius: 12,
                      resize: "none",
                    }}
                  />
                </Form.Group>
              </div>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            onClick={() => submitStatus("Rejected")}
            disabled={!canAdminUpdateStatus}
            style={{
              borderRadius: 10,
              minWidth: 120,
              background: "#DC2626",
              borderColor: "#DC2626",
              fontWeight: 700,
            }}
          >
            {savingStatus ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              "Rejected"
            )}
          </Button>

          <Button
            onClick={() => submitStatus("Approved")}
            disabled={!canAdminUpdateStatus}
            style={{
              borderRadius: 10,
              minWidth: 120,
              background: "#16A34A",
              borderColor: "#16A34A",
              fontWeight: 700,
            }}
          >
            {savingStatus ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              "Approved"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
